import _ from 'lodash';
import React, { Component } from 'react';
import { Button, FormCheck, Modal } from 'react-bootstrap';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import ReactTable from 'react-table';
import { ILostSaleCandidate } from '../../typings/LineItem';
import toastr from '../../../toastr';

interface IProps {
  candidateParts: ILostSaleCandidate[];
  container: Object;
  hide: () => void;
  lostSaleReasonCodes: Array<{ code: string; desc: string }>;
  onProcess: (parts: Array<{ partNum: string; qty: number }>, reasonCode: string) => void;
  show: boolean;
}

// We want case insensitive and searching within strings
const filterOverride = ({ id, value }: { id: string; value: string }, row: string[]) => {
  return row[id] ? row[id].toLowerCase().includes(value.toLowerCase()) : false;
};

@observer
class LostSaleReasonCodeSelectionModal extends Component<IProps, {}> {
  @observable selectedReasonCode = '';
  @observable partsChecked = new Set<number>(); // part uniqueIDs

  process() {
    if (this.selectedReasonCode === '') {
      toastr.error('Error', 'Please select a reason for this lost sale.');
      return;
    } else if (this.partsChecked.size === 0) {
      toastr.error('Error', 'Please select at least one part to record.');
      return;
    }

    const parts = this.props.candidateParts
      .filter(o => this.partsChecked.has(o.uniqueID))
      .map(o => ({ partNum: o.partNum, qty: o.qtyRequested }));

    this.props.onProcess(parts, this.selectedReasonCode);
  }

  selectAll() {
    if (this.selectedReasonCode === 'legacy') {
      this.selectInsufficientQty();
    } else {
      runInAction(() => {
        this.props.candidateParts.forEach(part => {
          this.partsChecked.add(part.uniqueID);
        });
      });
    }
  }

  selectInsufficientQty() {
    runInAction(() => {
      this.partsChecked.clear();
      this.props.candidateParts.forEach(part => {
        if (part.qtyInsufficient) {
          this.partsChecked.add(part.uniqueID);
        }
      });
    });
  }

  render() {
    /* This ostensibly no-op line triggers mobx to re-render on partChecked set changes.
       For example, from "Select All".

       The only read of the set is deep within a react-table cell render function that MobX
       can't detect.  We require react-controlled checkbox inputs for react-table filters
       to work correctly, and are using MobX to manage that state with an obsevable set.
       This line tells MobX to observe partsChecked and re-render on changes.
    */
    this.partsChecked.has(0);

    // A reason code of "legacy" indicates mirroring the classic QM method of recording only stocking parts
    // whose quantity is insufficient to meet the quantity requested by the clerk.
    const { candidateParts, container, hide, lostSaleReasonCodes, show } = this.props;
    const noCandidateParts = candidateParts.length === 0;

    return (
      <Modal
        backdrop="static"
        container={container as any}
        dialogClassName="sk-lostSaleReasonCodeSelectionModal"
        onHide={hide as any}
        show={show}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Lost Sale Reason / Part Selection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {noCandidateParts ? (
            <div>No parts to record.</div>
          ) : (
            <>
              <div
                className={`sk-reasonCode${this.selectedReasonCode === 'legacy' ? ' sk-reasonCodeActive' : ''}`}
                onClick={() => {
                  this.selectedReasonCode = 'legacy';
                  this.selectInsufficientQty();
                }}
              >
                Insufficient Quantity
              </div>
              {lostSaleReasonCodes.map((reasonCode, index) => (
                <div
                  className={`sk-reasonCode${
                    this.selectedReasonCode === reasonCode.code ? ' sk-reasonCodeActive' : ''
                  }`}
                  key={`sk-lsrc-${index}`}
                  onClick={() => (this.selectedReasonCode = reasonCode.code)}
                >
                  {reasonCode.desc}
                </div>
              ))}
              <hr />
              <ReactTable
                className="-striped"
                data={candidateParts}
                defaultFilterMethod={filterOverride}
                defaultPageSize={candidateParts.length}
                showPagination={false}
                columns={[
                  {
                    Cell: props => {
                      const uniqueID = props.original.uniqueID;

                      return (
                        <FormCheck inline={true}>
                          <FormCheck.Input
                            type="checkbox"
                            className="sk-useMaterialInput"
                            onChange={(evt: any) => {
                              if (evt.target.checked) {
                                this.partsChecked.add(uniqueID);
                              } else {
                                this.partsChecked.delete(uniqueID);
                              }
                            }}
                            checked={this.partsChecked.has(uniqueID)}
                            disabled={this.selectedReasonCode === 'legacy' && !props.original.qtyInsufficient}
                          />
                        </FormCheck>
                      );
                    },
                    sortable: false,
                    style: {
                      textAlign: 'center',
                    },
                    width: 30,
                  },
                  {
                    Header: 'Req',
                    accessor: 'qtyRequested',
                    style: {
                      textAlign: 'center',
                    },
                    width: 60,
                  },
                  {
                    Cell: props => {
                      return props.original.confirmationSale ? (
                        <span title="Confirmation Sale">C</span>
                      ) : props.original.qtyInsufficient ? (
                        <span style={{ color: 'red', fontWeight: 'bold' }}>{props.value}</span>
                      ) : (
                        props.value
                      );
                    },
                    Header: 'OH',
                    accessor: 'onHand',
                    style: {
                      textAlign: 'center',
                    },
                    width: 60,
                  },
                  {
                    Cell: props => {
                      return <span title={props.original.partNum}>{props.value}</span>;
                    },
                    Header: 'Part',
                    accessor: 'partNum',
                    filterable: true,
                  },
                  {
                    Cell: props => {
                      return <span title={props.original.longDescription}>{props.value}</span>;
                    },

                    Header: 'Description',
                    accessor: 'description',
                    filterable: true,
                  },
                ]}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="sk-lostSaleReasonCodeSelectionModalFooter ">
            <div>
              {!noCandidateParts && (
                <Button onClick={() => this.selectAll()} variant="secondary">
                  Select All
                </Button>
              )}
            </div>
            <div>
              <Button variant="default" onClick={hide}>
                Cancel
              </Button>
              {!noCandidateParts && (
                <Button
                  variant="success"
                  onClick={() => {
                    this.process();
                  }}
                >
                  Process
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default LostSaleReasonCodeSelectionModal;
